/**
 * The floating "Fusion AI" button on every signed-in page and its chat panel: a 420px right drawer on desktop (the page
 * stays usable beside it), full screen on a phone. The open conversation id is remembered in localStorage.
 */
import { useQuery } from "@tanstack/react-query";
import { Link, useRouterState } from "@tanstack/react-router";
import { Maximize2, Plus, Sparkles, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { AK, isBrief, loadOpenId, saveOpenId, sortRecent, type AsState } from "./api";
import { BriefMenu } from "./BriefMenu";
import { Conversation } from "./Chat";

export function useAssistantState(enabled = true) {
  return useQuery({ queryKey: AK.state, queryFn: () => api<AsState>("/api/assistant/state"), enabled, staleTime: 30_000 });
}

export function FusionAiButton() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [open, setOpen] = useState(false);
  const [convId, setConvIdRaw] = useState<string | null>(() => loadOpenId());
  const setConvId = useCallback((id: string | null) => { setConvIdRaw(id); saveOpenId(id); }, []);
  const state = useAssistantState(open);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const hidden = path === "/login" || path === "/assistant" || path.startsWith("/assistant/");

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { setOpen(false); btnRef.current?.focus(); } };
    document.addEventListener("keydown", onKey);
    panelRef.current?.querySelector<HTMLTextAreaElement>("textarea")?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);
  useEffect(() => { if (hidden) setOpen(false); }, [hidden]);

  if (hidden) return null;
  const recent = sortRecent(state.data?.conversations ?? []).slice(0, 25);
  const known = !convId || recent.some((c) => c.id === convId);
  const briefs = recent.filter(isBrief);
  const chats = recent.filter((c) => !isBrief(c));

  return (
    <>
      {!open && (
        <button
          ref={btnRef}
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open Fusion AI"
          className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom))] right-4 z-30 inline-flex items-center gap-2 rounded-full bg-brand-600 px-3.5 py-3 text-sm font-semibold text-white shadow-[0_8px_24px_-6px_rgb(43_53_255/0.6)] transition hover:bg-brand-700 md:bottom-6 md:right-6 md:py-2.5"
        >
          <Sparkles size={18} />
          <span className="hidden sm:inline">Fusion AI</span>
        </button>
      )}
      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Fusion AI"
          className="fixed inset-0 z-50 flex flex-col bg-[var(--panel)] pb-[env(safe-area-inset-bottom)] md:bottom-0 md:left-auto md:right-0 md:top-16 md:w-[420px] md:border-l md:border-[var(--line)] md:shadow-[var(--shadow-pop)]"
        >
          <header className="flex shrink-0 items-center gap-1.5 border-b border-[var(--line)] px-3 py-2.5">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-600"><Sparkles size={16} /></span>
            <select
              aria-label="Recent conversations"
              className="input !min-h-0 min-w-0 flex-1 truncate py-1.5 text-sm font-semibold"
              value={convId ?? ""}
              onChange={(e) => setConvId(e.target.value || null)}
            >
              <option value="">{convId ? "New chat" : "Fusion AI — new chat"}</option>
              {!known && convId && <option value={convId}>Current conversation</option>}
              {briefs.length > 0 && (
                <optgroup label="From Fusion AI">
                  {briefs.map((c) => <option key={c.id} value={c.id}>{c.title || "Fusion AI"}</option>)}
                </optgroup>
              )}
              {briefs.length > 0 && chats.length > 0 ? (
                <optgroup label="Your conversations">
                  {chats.map((c) => <option key={c.id} value={c.id}>{c.title || "Untitled"}</option>)}
                </optgroup>
              ) : chats.map((c) => <option key={c.id} value={c.id}>{c.title || "Untitled"}</option>)}
            </select>
            <BriefMenu iconOnly onOpen={setConvId} />
            <button type="button" className="btn btn-ghost btn-icon !h-8 !min-h-0 !w-8 shrink-0" aria-label="New chat" title="New chat" onClick={() => setConvId(null)}><Plus size={17} /></button>
            <Link
              to={convId ? "/assistant/$id" : "/assistant"}
              params={convId ? { id: convId } : {}}
              className="btn btn-ghost btn-icon !h-8 !min-h-0 !w-8 shrink-0"
              aria-label="Open full page"
              title="Open full page"
              onClick={() => setOpen(false)}
            >
              <Maximize2 size={15} />
            </Link>
            <button type="button" className="btn btn-ghost btn-icon !h-8 !min-h-0 !w-8 shrink-0" aria-label="Close Fusion AI" title="Close (Esc)" onClick={() => setOpen(false)}><X size={18} /></button>
          </header>
          <div className="min-h-0 flex-1">
            <Conversation id={convId} compact onCreated={setConvId} onMissing={() => setConvId(null)} />
          </div>
        </div>
      )}
    </>
  );
}
